/**
 * Graphique donut SVG avec légende
 * data: [{ label, value }]
 */
export default function DonutChart({
  title,
  data = [],
  colors = ["#203a8a", "#3b82f6", "#8b5cf6", "#10b981", "#f59e0b", "#ef4444", "#ec4899", "#06b6d4"],
  loading = false,
  emptyMessage = "Aucune donnée disponible",
  centerLabel,
  valueFormatter = (v) => v,
}) {
  const total = data.reduce((sum, d) => sum + (d.value || 0), 0)
  const size = 180
  const strokeWidth = 28
  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius

  let cumulative = 0

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
      {title && (
        <h3 className="text-lg font-semibold text-[#203a8a] mb-4">{title}</h3>
      )}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#203a8a]"></div>
        </div>
      ) : data.length === 0 || total === 0 ? (
        <div className="flex items-center justify-center h-48 text-gray-500 text-sm">
          {emptyMessage}
        </div>
      ) : (
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="relative flex-shrink-0">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
              <circle
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke="#f3f4f6"
                strokeWidth={strokeWidth}
              />
              {data.map((item, i) => {
                const value = item.value || 0
                const fraction = value / total
                const dashArray = fraction * circumference
                const color = colors[i % colors.length]
                const offset = -cumulative * circumference
                cumulative += fraction
                return (
                  <circle
                    key={i}
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    fill="none"
                    stroke={color}
                    strokeWidth={strokeWidth}
                    strokeDasharray={`${dashArray} ${circumference - dashArray}`}
                    strokeDashoffset={offset}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                  />
                )
              })}
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <p className="text-2xl font-bold text-[#203a8a]">
                {valueFormatter(total)}
              </p>
              {centerLabel && (
                <p className="text-xs text-gray-500">{centerLabel}</p>
              )}
            </div>
          </div>
          <div className="flex-1 space-y-2 w-full">
            {data.map((item, i) => {
              const percent = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0
              return (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: colors[i % colors.length] }}
                    />
                    <span className="text-gray-700 truncate">{item.label}</span>
                  </div>
                  <div className="text-right ml-2">
                    <span className="font-semibold text-gray-900">
                      {valueFormatter(item.value)}
                    </span>
                    <span className="text-gray-500 ml-1">({percent}%)</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
